import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminUsers, type UserListItem } from "../../hooks/useAdminUsers";

/**
 * UserListPage - Admin page to view and manage all users.
 *
 * Features:
 * - Paginated user list with real data
 * - Search by email, name, or ID
 * - Filter by tier and status
 * - Quick actions per user
 */
export function UserListPage() {
  const [searchInput, setSearchInput] = useState("");
  const {
    users,
    pagination,
    isLoading,
    error,
    filters,
    updateSearch,
    updateFilters,
    goToPage,
  } = useAdminUsers();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    updateSearch(value);
  };

  const handleTierChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as "" | "free" | "family" | "classroom";
    updateFilters({ tier: value || undefined });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as "" | "active" | "canceled" | "past_due";
    updateFilters({ status: value || undefined });
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage parent accounts and subscriptions
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={handleSearchChange}
                placeholder="Search by email, name, or ID..."
                className="
                  w-full pl-10 pr-4 py-2
                  bg-gray-50 border border-gray-200 rounded-lg
                  text-sm placeholder-gray-400
                  focus:ring-2 focus:ring-magic-500 focus:border-transparent
                  focus:bg-white transition-colors
                "
              />
            </div>
          </div>
          <select
            value={filters.tier || ""}
            onChange={handleTierChange}
            className="
              px-4 py-2
              bg-gray-50 border border-gray-200 rounded-lg
              text-sm text-gray-700
              focus:ring-2 focus:ring-magic-500 focus:border-transparent
            "
          >
            <option value="">All Tiers</option>
            <option value="free">Free</option>
            <option value="family">Family</option>
            <option value="classroom">Classroom</option>
          </select>
          <select
            value={filters.status || ""}
            onChange={handleStatusChange}
            className="
              px-4 py-2
              bg-gray-50 border border-gray-200 rounded-lg
              text-sm text-gray-700
              focus:ring-2 focus:ring-magic-500 focus:border-transparent
            "
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="canceled">Canceled</option>
            <option value="past_due">Past Due</option>
          </select>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* User table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Subscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Children
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-48" />
                      <div className="h-3 bg-gray-100 rounded w-32 mt-2" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 bg-gray-200 rounded-full w-20" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-8" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-24" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-16 ml-auto" />
                    </td>
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <EmptyIcon />
                      <p className="text-sm">
                        {filters.search || filters.tier || filters.status
                          ? "No users match your filters"
                          : "No users found"}
                      </p>
                      {(filters.search || filters.tier || filters.status) && (
                        <button
                          onClick={() => {
                            setSearchInput("");
                            updateFilters({
                              search: undefined,
                              tier: undefined,
                              status: undefined,
                            });
                          }}
                          className="text-xs text-magic-600 hover:text-magic-700 font-medium"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((user) => <UserRow key={user.id} user={user} />)
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing{" "}
            <span className="font-medium">
              {users.length > 0
                ? (pagination.page - 1) * pagination.limit + 1
                : 0}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(
                pagination.page * pagination.limit,
                pagination.total
              )}
            </span>{" "}
            of <span className="font-medium">{pagination.total}</span> users
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => goToPage(pagination.page - 1)}
              disabled={pagination.page <= 1 || isLoading}
              className="
                px-3 py-1.5 text-sm font-medium
                text-gray-700 bg-white border border-gray-200 rounded-lg
                hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {getPageNumbers(pagination.page, pagination.totalPages).map(
                (pageNum, i) =>
                  pageNum === "..." ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                      ...
                    </span>
                  ) : (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum as number)}
                      disabled={isLoading}
                      className={`
                        px-3 py-1.5 text-sm font-medium rounded-lg transition-colors
                        ${
                          pageNum === pagination.page
                            ? "bg-magic-600 text-white"
                            : "text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
                        }
                      `}
                    >
                      {pageNum}
                    </button>
                  )
              )}
            </div>
            <button
              onClick={() => goToPage(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages || isLoading}
              className="
                px-3 py-1.5 text-sm font-medium
                text-gray-700 bg-white border border-gray-200 rounded-lg
                hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- Helper Components -----

function UserRow({ user }: { user: UserListItem }) {
  const displayName = user.name || user.email?.split("@")[0] || "Unknown";
  const joinedDate = new Date(user.createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year:
      new Date(user.createdAt).getFullYear() !== new Date().getFullYear()
        ? "numeric"
        : undefined,
  });

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-magic-100 flex items-center justify-center">
            <span className="text-magic-600 font-semibold text-sm">
              {displayName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{displayName}</p>
            <p className="text-xs text-gray-500">{user.email || user.id}</p>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <TierBadge
          tier={user.subscription?.tier || "free"}
          status={user.subscription?.status}
        />
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-gray-900">{user.childCount}</span>
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-gray-500">{joinedDate}</span>
      </td>
      <td className="px-6 py-4 text-right">
        <Link
          to={`/admin/users/${user.id}`}
          className="text-sm font-medium text-magic-600 hover:text-magic-700"
        >
          View
        </Link>
      </td>
    </tr>
  );
}

function TierBadge({
  tier,
  status,
}: {
  tier: "free" | "family" | "classroom";
  status?: string;
}) {
  const tierStyles = {
    free: "bg-gray-100 text-gray-700",
    family: "bg-magic-100 text-magic-700",
    classroom: "bg-sunshine-100 text-sunshine-700",
  };

  const tierLabels = {
    free: "Free",
    family: "Family",
    classroom: "Classroom",
  };

  const isActive = status === "active" || status === "trialing";

  return (
    <div className="flex items-center gap-2">
      <span
        className={`
          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
          ${tierStyles[tier]}
        `}
      >
        {tierLabels[tier]}
      </span>
      {status && status !== "active" && (
        <span
          className={`
            inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
            ${
              status === "canceled"
                ? "bg-red-100 text-red-700"
                : status === "past_due"
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-blue-100 text-blue-700"
            }
          `}
        >
          {status === "past_due" ? "Past Due" : status}
        </span>
      )}
    </div>
  );
}

function getPageNumbers(
  current: number,
  total: number
): (number | "...")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  if (current <= 3) {
    return [1, 2, 3, 4, 5, "...", total];
  }

  if (current >= total - 2) {
    return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
  }

  return [1, "...", current - 1, current, current + 1, "...", total];
}

// ----- Icons -----

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function EmptyIcon() {
  return (
    <svg
      className="w-12 h-12 text-gray-300"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

export default UserListPage;
